<template>
    <div class="app-container mt-5">
        <div class="page-title mb-3">
            <router-link to="/configuration">
                <span class="breadcrumb-link">
                    <v-icon class="breadcrumb-link-icon">settings</v-icon> Configuration du compte
                </span>
            </router-link>
            <v-icon>keyboard_arrow_right</v-icon>
            <span class="breadcrumb-item">
                <v-icon class="breadcrumb-item-icon">feed</v-icon> Mes modèles de formulaires
            </span>
        </div>

        <div class="row" v-if="!$global.loading">
            <div class="col-3">
                <v-alert text class="mb-5">
                    Les modèles de formulaires permettent de définir l'ensemble des champs disponibles dans les
                    formulaires.
                    Il est possible de configurer chaque champs, en renommant le titre du champ ou en proposant une
                    aide à la saisie.
                </v-alert>
            </div>
            <div class="col">

                <v-card class="p-4 mb-5">
                    <v-card-title class="p-0 mb-5">Formulaires Dahub - Campagnes de mise à jour</v-card-title>

                    <div class="d-flex">
                        <v-btn outlined @click="prepareCreate('CMAJ')" class="mb-5 custom-button-primary">
                            <v-icon left>add</v-icon> Créer un modèle
                        </v-btn>
                    </div>

                    <div v-if="cmajTemplates.length">
                        <v-data-table :headers="headers" :items="cmajTemplates" :loading="loading" class="border"
                            :footer-props="{
                                itemsPerPageAllText: 'Toutes',
                                itemsPerPageText: 'Modèles par page',
                                itemsPerPageOptions: [10, 20, 50, 100]
                            }">
                            <template v-slot:item.name="{ item }">
                                <a @click="prepareEdit(item)">{{ item.name }}</a>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                                {{ formatDate(item.created_at, false) }}
                            </template>
                            <template v-slot:item.updated_at="{ item }">
                                {{ formatDate(item.web_form_configuration.updated_at, false) }}
                            </template>
                            <template v-slot:item.user="{ item }">
                                {{ item.user.first_name }} {{ item.user.last_name }}
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn small icon v-on="on" @click="prepareDuplication(item)" class="mr-2">
                                            <v-icon>content_copy</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Dupliquer</span>
                                </v-tooltip>
                                <v-btn small icon class="custom-button-error" @click="deleteTemplate(item)">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </div>

                    <div v-else class="text-center">
                        <small>Aucun modèle n'a été ajouté pour le moment</small>
                    </div>

                </v-card>

                <v-card class="p-4" v-if="uskaContract">
                    <v-card-title class="p-0 mb-5">Formulaire Uska - Formulaire de mise à jour autonome</v-card-title>

                    <div class="d-flex">
                        <div v-if="!openFormTemplate">
                            <v-btn v-if="!openFormTemplate"
                                   outlined
                                   @click="prepareCreate('OPEN_FORM')"
                                   class="mb-5 custom-button-primary">
                                <v-icon left>add</v-icon>
                                Créer un modèle
                            </v-btn>
                        </div>

                        <div v-else class="d-flex">
                            <div class="mt-auto mb-auto">
                                Créé le {{ formatDate(openFormTemplate.created_at) }} par
                                {{ openFormTemplate.user.first_name }} {{ openFormTemplate.user.last_name }}
                            </div>
                            <v-btn outlined
                                   @click="prepareEdit(openFormTemplate)"
                                   class="custom-button-primary ml-5">
                                <v-icon left>edit</v-icon>
                                Modifier
                            </v-btn>
                        </div>

                    </div>
                </v-card>

            </div>
        </div>

        <v-dialog content-class="fullscreen-dialog" fullscreen persistent hide-overlay v-model="openTemplateDialog">
            <v-toolbar dark class="bg-primary">
                <v-toolbar-title style="min-width: 400px">
                    <div v-if="!editMode">Création d'un modèle de formulaire</div>
                    <div v-else>Modification d'un modèle de formulaire</div>
                </v-toolbar-title>

                <div class="text-right w-100">
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-toolbar>

            <div class="p-5">
                <v-text-field v-if="currentTemplate.type === 'CMAJ'" outlined label="Nom du modèle" style="padding-top: 0!important;" hide-details
                    v-model="currentTemplate.name" class="subject-input mb-5">
                </v-text-field>

                <information :tabulations="tabulations"
                             :select-all="true"
                             :hide-next-button="true"
                             :hide-top-info="true"></information>

                <div class="d-flex justify-content-end gap-10 mt-5">
                    <v-btn @click="openTemplateDialog = false">Annuler</v-btn>
                    <v-btn color="primary" v-if="!editMode" @click="createTemplate"
                        :disabled="currentTemplate.name === null || currentTemplate.name === ''">
                        Créer
                    </v-btn>
                    <v-btn color="primary" v-else @click="updateTemplate"
                        :disabled="currentTemplate.name === null || currentTemplate.name === ''">
                        Enregistrer
                    </v-btn>
                </div>
            </div>
        </v-dialog>

        <v-dialog v-model="copyModale" max-width="400" style="{ 'zIndex': 200 }" @keydown.esc="copyModale = false">
            <v-card>
                <v-toolbar dense flat>
                    <v-toolbar-title>Duplication d'un modèle de formulaire</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pa-4">
                    <v-text-field
                      outlined
                      label="Nom du modèle"
                      v-model="duplicatedName"
                      hide-details
                    ></v-text-field>
                </v-card-text>

                <v-card-actions class="pt-0">
                    <v-spacer></v-spacer>
                    <v-btn @click="copyModale = false">
                        Annuler
                    </v-btn>
                    <v-btn class="default-button" @click="duplicateTemplate()">
                        Dupliquer
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <confirm ref="confirm"></confirm>
    </div>
</template>

<script>
import Information from "../../campaign/cmaj/stepers/Information"
import { mapMutations, mapState } from "vuex";
import Confirm from "../../tools/Confirm"

export default {
    name: "WebFormTemplateConfig",
    components: { Confirm, Information },
    data() {
        return {
            loading: false,
            templates: [],
            tabulations: [],
            baseTabulations: [],
            openTemplateDialog: false,
            currentTemplate: {
                name: null,
                fields: []
            },
            editMode: false,
            headers: [
                {
                    text: 'Nom',
                    value: 'name'
                },
                {
                    text: 'Créé le',
                    value: 'created_at'
                },
                {
                    text: 'Dernière modification',
                    value: 'updated_at'
                },
                {
                    text: 'Propriétaire',
                    value: 'user'
                },
                {
                    text: '',
                    value: 'actions'
                },
            ],
            copyModale: false,
            duplicatedName: null,
            duplicatedId: null,
        }
    },
    methods: {
        ...mapMutations('createCmaj', ['setEmailsValid', 'addEditedTabulation', 'setTxtIntro', 'setTxtIntroAspect',
        'setTxtIntroCmaj',]),
        getTemplates() {
            this.$global.loading = true
            this.loading = true

            this.$http.get('auth/account/web-form-templates').then(res => {
                this.templates = res.body.data.templates

                this.$global.loading = false
                this.loading = false
            })
        },
        createTemplate() {
            this.$global.loading = true

            let payload = {
                name: this.currentTemplate.name,
                fieldIds: this.getFieldIds(),
                type : this.currentTemplate.type,
                editedTabulations: this.editedTabulations,
                txtIntro: this.txtIntro,
            }

            this.$http.post('auth/account/web-form-templates', payload).then(res => {
                this.getTemplates()

                this.openTemplateDialog = false
                this.$global.loading = false

                this.currentTemplate = {
                    name: null,
                    fields: [],
                    type: null
                }
            })
        },
        prepareEdit(template) {
            this.$global.loading = true
            this.editMode = true
            this.currentTemplate = JSON.parse(JSON.stringify(template))
            this.tabulations = JSON.parse(JSON.stringify(template.tabulations))
            this.setTxtIntro(JSON.parse(JSON.stringify(template.web_form_configuration.text_intro)))

            this.tabulations.forEach(tab => {
                if(tab.help_description || tab.new_title) {
                    this.addEditedTabulation(tab)
                }
            })

            setTimeout(() => {
                this.$global.$emit('reload-info')
            }, 200)

            setTimeout(() => {
                this.openTemplateDialog = true

                this.$global.loading = false
            }, 1000)
        },
        deleteTemplate(template) {
            this.$refs.confirm.open('Attention',
                'Vous allez supprimer le modèle de formulaire <b>' + template.name,
                { color: 'red' }).
                then((confirm) => {
                    if (confirm) {
                        this.$global.loading = true

                        this.$http.delete('auth/account/web-form-templates/' + template.id).then(res => {
                            this.getTemplates()

                            this.$global.loading = false
                        })
                    }
                })
        },
        prepareCreate(type) {
            this.$global.loading = true

            this.currentTemplate = {
                name: null,
                fields: [],
                type: type
            }

            this.setTxtIntro(this.account.configurations.txtIntro.value)

            if (type !== 'CMAJ') {
                this.currentTemplate.name = 'Formulaire Uska'
            }

            this.tabulations = JSON.parse(JSON.stringify(this.baseTabulations))

            setTimeout(() => {
                this.$global.$emit('reload-info')
            }, 200)

            setTimeout(() => {
                this.editMode = false
                this.openTemplateDialog = true

                this.$global.loading = false
            }, 1000)
        },
        updateTemplate() {
            this.$global.loading = true

            let payload = {
                templateId: this.currentTemplate.id,
                name: this.currentTemplate.name,
                fieldIds: this.getFieldIds(),
                type: this.currentTemplate.type,
                editedTabulations: this.editedTabulations,
                txtIntro: this.txtIntro,
            }

            this.$http.post('auth/account/web-form-templates/update', payload).then(res => {
                this.getTemplates()

                this.openTemplateDialog = false
                this.$global.loading = false

                this.currentTemplate = {
                    name: null,
                    fields: [],
                    type: null
                }
            })
        },
        getFields() {
            this.$http.get('auth/campaigns/cmaj/referential/fields').then(res => {
                this.tabulations = res.body.data.tabulations
                this.baseTabulations = res.body.data.tabulations
            })
        },
        getFieldIds() {
            let fieldsArray = Object.values(this.selectedFields)
            fieldsArray = [].concat.apply([], fieldsArray)

            return fieldsArray.map(field => {
                return {
                    new_title: field.new_title,
                    help_description: field.help_description,
                    id: field.field_id,
                    required: field.required,
                }
            })
        },
        closeDialog() {
            this.openTemplateDialog = false
        },
        getAccountIntroTxt() {
            if(this.account.configurations.txtIntroAspect) {
                this.setTxtIntroAspect(this.account.configurations.txtIntroAspect.value)
            }
            this.setTxtIntro(this.account.configurations.txtIntro.value)
            this.setTxtIntroCmaj(this.account.configurations.txtIntro.value)
        },
        duplicateTemplate() {
            let payload = {
                webFormTemplateId: this.duplicatedId,
                name: this.duplicatedName,
            }

            this.$global.loading = true

            this.$http.post("auth/account/web-form-templates/duplicate", payload).then(res => {
                this.copyModale = false
                this.$global.loading = false
                this.getTemplates()
            })
        },
        prepareDuplication(template) {
            this.duplicatedId = template.id
            this.duplicatedName = template.name + ' (Copie)'

            this.copyModale = true
        },
    },
    computed: {
        ...mapState('createCmaj', ['selectedFields', 'editedTabulations','txtIntro']),
        ...mapState('account', ['account', 'uskaContract']),
        cmajTemplates() {
            return this.templates.filter(template => template.type === 'CMAJ')
        },
        openFormTemplate() {
            return this.templates.find(template => template.type === 'OPEN_FORM')
        }
    },
    created() {
        this.$store.dispatch('resource/fetchCategories')

        this.setEmailsValid([])

        this.getTemplates()
        this.getFields()
        this.getAccountIntroTxt()
    }
}
</script>